import { Component, Inject, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import {
    SessionTimeoutWarningConfig
} from "../model/session-tineout-warning.config.model";
import { SessionHandlerService, SessionHandlerServiceToken } from "../service/session-handler.service";
import { selectSessionState, SessionState } from "../model/session.state.model";
import { Store } from "@ngrx/store";

/**
 * @whatItDoes This component is a warning panel that appears after the user is inactive for a while and session timeout
 * is near. It informs the user, that if he/she remains inactive then the session will end soon and offers two buttons
 * to either postpone the session timeout, or log out immediately.
 *
 * @howToUse
 *
 * ```
 <session-timeout-warning>
 </session-timeout-warning>
 * ```
 *
 * @description
 *
 * A simple warning panel about an imminent session timeout.
 *
 * @selector "session-timeout-warning"
 * @ngModule SessionComponentModule
 */
@Component({
    selector: "session-timeout-warning",
    template: `
        <div class="session-timeout-warning" *ngIf="isVisibleTimeout">
            <div class="session-timeout-warning-content">
                <img class="session-timeout-icon" src="assets/icons/spot_exclamation_stroke.svg" />
                <div class="session-warning-text">
                  {{'mkb.card3dsecure.modal.session.timeout.warning.text' | resolve : {inactivity : inactivityText, timeout : timeoutText} }}
                </div>
                <div class="session-timeout-warning-counter">
                  {{"mkb.card3dsecure.modal.session.timeout.warning.text2" | resolve  : {timeout : timeoutText} }}
                </div>
                <div class="session-button-wrapper"
                     fxLayoutAlign="center center">
                    <button mat-flat-button color="primary" class="left"
                            (click)="onProlong()">
                        {{"mkb.card3dsecure.modal.session.timeout.warning.prolong.button" | resolve}}
                    </button>
                    <button mat-flat-button color="basic"
                            (click)="onEnd()">
                      {{"mkb.card3dsecure.modal.session.timeout.warning.logout.button" | resolve}}
                    </button>
                </div>
            </div>
        </div>`
})

export class SessionTimeoutWarningComponent implements OnDestroy {
    public _config: SessionTimeoutWarningConfig = {
        timeoutWarningTime: 60
    };

    private _isVisible = false;
    private _inactivityText: string;
    private _timeoutText: string;
    public _sessionHandler: SessionHandlerService;

    public _sessionObservableSubscription: Subscription;

    constructor(ngrxStore: Store<any>,
                @Inject(SessionHandlerServiceToken)
                    sessionHandler: SessionHandlerService) {
        this._sessionHandler = sessionHandler;

        this._sessionObservableSubscription = ngrxStore.select(selectSessionState).subscribe(this.processSessionState.bind(this));
    }

    public onEnd(): void {
        this._sessionHandler.logout();
    }

    public onProlong(): void {
        this._sessionHandler.reviveSession();
    }

    public processSessionState(state: SessionState) {
        if (state) {
            this._isVisible = state.remainingTime > 0 && state.warningTimeOut > -1 && state.remainingTime <= state.warningTimeOut;
            this._timeoutText = this.secondsToText(Math.max(state.remainingTime, 0), true);
            this._inactivityText = this.secondsToText(Math.max(state.sessionTime - state.warningTimeOut, 0), true);
        }
    }

    public secondsToText(seconds: number, onlyMinutes?: boolean) {
        const minutes: number = onlyMinutes ? Math.ceil(seconds / 60) : Math.floor(seconds / 60);
        const secs: number = seconds - minutes * 60;

        const pad = function pad(num: number) {
            return ("00" + num).slice(-2);
        };

        return onlyMinutes ? "" + minutes : pad(minutes) + ":" + pad(secs);
    }

    ngOnDestroy(): void {
        if (this._sessionObservableSubscription) {
            this._sessionObservableSubscription.unsubscribe();
            this._sessionObservableSubscription = null;
        }
    }

    public get isVisibleTimeout(): boolean {
        return this._isVisible;
    }

    public get timeoutText(): string {
        return this._timeoutText;
    }

    public get inactivityText(): string {
      return this._inactivityText;
    }
}
